import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cvAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, Sparkles, Target, BarChart3, Loader2, 
  Copy, Check, AlertCircle, TrendingUp, Zap, FileText,
  CheckCircle, XCircle, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const CVEnhancerPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [activeTab, setActiveTab] = useState('achievements');
  const [cvs, setCvs] = useState([]);
  const [selectedCvId, setSelectedCvId] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Achievement Generator State
  const [duty, setDuty] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [achievements, setAchievements] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  
  // Keyword Matcher State
  const [jobDescription, setJobDescription] = useState('');
  const [keywords, setKeywords] = useState(null);
  
  // CV Score State
  const [cvScore, setCvScore] = useState(null);

  useEffect(() => {
    fetchCVs();
  }, []);

  const fetchCVs = async () => {
    try {
      const res = await cvAPI.list();
      setCvs(res.data);
      if (res.data.length > 0) {
        setSelectedCvId(res.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch CVs:', error);
    }
  };

  const handleGenerateAchievements = async () => {
    if (!duty.trim()) {
      toast.error('Please enter a job duty');
      return;
    }
    setLoading(true);
    setAchievements(null);
    try {
      const res = await cvAPI.generateAchievement({
        duty: duty,
        job_title: jobTitle || null
      });
      setAchievements(res.data);
      toast.success('Achievements generated!');
    } catch (error) {
      toast.error('Failed to generate achievements');
    } finally {
      setLoading(false);
    }
  };

  const handleMatchKeywords = async () => {
    if (!jobDescription.trim()) {
      toast.error('Please paste a job description');
      return;
    }
    setLoading(true);
    setKeywords(null);
    try {
      const res = await cvAPI.matchKeywords({
        job_description: jobDescription,
        cv_id: selectedCvId || null
      });
      setKeywords(res.data);
      toast.success('Keywords extracted!');
    } catch (error) {
      toast.error('Failed to analyze keywords');
    } finally {
      setLoading(false);
    }
  };

  const handleScoreCV = async () => {
    if (!selectedCvId) {
      toast.error('Please select a CV');
      return;
    }
    setLoading(true);
    setCvScore(null);
    try {
      const res = await cvAPI.getScore(selectedCvId);
      setCvScore(res.data);
    } catch (error) {
      toast.error('Failed to score CV');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <Sparkles className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">CV Enhancement Tools</h2>
          <p className="text-slate-600 mb-6">Upgrade your CV with AI-powered tools. Subscribe to access.</p>
          <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
            Subscribe Now - R25 for 30 Days
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">CV Enhancement Tools</h1>
            <p className="text-sm text-slate-500">AI-powered tools to make your CV stand out</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'achievements', label: 'Achievement Generator', icon: Sparkles },
            { id: 'keywords', label: 'Keyword Matcher', icon: Target },
            { id: 'score', label: 'CV Score Checker', icon: BarChart3 }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Achievement Generator */}
        {activeTab === 'achievements' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Achievement Generator</h2>
                  <p className="text-slate-500 text-sm">Turn boring job duties into impressive achievements with metrics</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Your Job Duty</label>
                  <Textarea
                    value={duty}
                    onChange={(e) => setDuty(e.target.value)}
                    placeholder="e.g., Responsible for customer service, Handled cash register, Managed inventory..."
                    className="min-h-[100px]"
                    data-testid="duty-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Job Title (optional)</label>
                  <Input
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="e.g., Sales Assistant, Receptionist, Cashier"
                  />
                </div>
                <Button
                  onClick={handleGenerateAchievements}
                  disabled={loading || !duty.trim()}
                  className="w-full bg-amber-500 hover:bg-amber-600"
                  data-testid="generate-achievements-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Generate Achievements</>
                  )}
                </Button>
              </div>
            </div>

            {achievements && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                <h3 className="font-bold text-slate-900 mb-4">Generated Achievements</h3>
                <div className="space-y-3">
                  {achievements.achievements?.map((ach, i) => (
                    <div 
                      key={i} 
                      className={`p-4 rounded-xl border ${
                        ach.strength === 'strong' ? 'bg-emerald-50 border-emerald-200' :
                        ach.strength === 'medium' ? 'bg-blue-50 border-blue-200' :
                        'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-slate-800 flex-1">{ach.text}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(ach.text, i)}
                          className="flex-shrink-0"
                        >
                          {copiedId === i ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <p className="text-sm text-slate-500 mt-2">
                        <span className={`font-medium ${
                          ach.strength === 'strong' ? 'text-emerald-600' :
                          ach.strength === 'medium' ? 'text-blue-600' : 'text-slate-600'
                        }`}>
                          {ach.strength?.toUpperCase()}
                        </span>
                        {' '} - {ach.tip}
                      </p>
                    </div>
                  ))}
                </div>
                {achievements.general_tip && (
                  <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <p className="text-sm text-amber-800">
                      <strong>Pro Tip:</strong> {achievements.general_tip}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Keyword Matcher */}
        {activeTab === 'keywords' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Keyword Matcher</h2>
                  <p className="text-slate-500 text-sm">Extract ATS keywords from job descriptions and match with your CV</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Paste Job Description</label>
                  <Textarea
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Paste the full job description here..."
                    className="min-h-[150px]"
                    data-testid="job-description-input"
                  />
                </div>
                {cvs.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Compare with your CV (optional)</label>
                    <Select value={selectedCvId} onValueChange={setSelectedCvId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a CV" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Don't compare</SelectItem>
                        {cvs.map((cv) => (
                          <SelectItem key={cv.id} value={cv.id}>
                            {cv.personal_details?.full_name || 'Untitled CV'} - {cv.cv_type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button
                  onClick={handleMatchKeywords}
                  disabled={loading || !jobDescription.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="match-keywords-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                  ) : (
                    <><Target className="w-4 h-4 mr-2" /> Extract Keywords</>
                  )}
                </Button>
              </div>
            </div>

            {keywords && (
              <div className="space-y-4">
                {/* Match Score */}
                {keywords.cv_match && (
                  <div className={`rounded-2xl p-6 ${
                    keywords.cv_match.match_percentage >= 70 ? 'bg-emerald-600' :
                    keywords.cv_match.match_percentage >= 50 ? 'bg-amber-500' : 'bg-red-500'
                  } text-white`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/80 text-sm">CV Match Score</p>
                        <p className="text-4xl font-bold">{keywords.cv_match.match_percentage}%</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white/80 text-sm">{keywords.cv_match.matched_keywords?.length || 0} matched</p>
                        <p className="text-white/80 text-sm">{keywords.cv_match.missing_keywords?.length || 0} missing</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Must-Have Keywords */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                  <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    Must-Have Keywords
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {keywords.must_have_keywords?.map((kw, i) => {
                      const isMatched = keywords.cv_match?.matched_keywords?.some(
                        m => m.toLowerCase() === kw.toLowerCase()
                      );
                      return (
                        <span 
                          key={i} 
                          className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1 ${
                            isMatched 
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                              : 'bg-red-100 text-red-700 border border-red-200'
                          }`}
                        >
                          {isMatched ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          {kw}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Required Skills */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                  <h3 className="font-bold text-slate-900 mb-3">Required Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {keywords.required_skills?.map((skill, i) => (
                      <span key={i} className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Missing Keywords */}
                {keywords.cv_match?.missing_keywords?.length > 0 && (
                  <div className="bg-red-50 rounded-2xl p-6 border border-red-200">
                    <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Missing from Your CV
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {keywords.cv_match.missing_keywords.slice(0, 10).map((kw, i) => (
                        <span key={i} className="px-3 py-1.5 bg-white text-red-700 rounded-full text-sm border border-red-200">
                          {kw}
                        </span>
                      ))}
                    </div>
                    <p className="text-red-700 text-sm mt-3">
                      Consider adding these keywords to your CV if they apply to your experience.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* CV Score Checker */}
        {activeTab === 'score' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">CV Score Checker</h2>
                  <p className="text-slate-500 text-sm">Get a detailed score of your CV against employer criteria</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Select CV to Score</label>
                  <Select value={selectedCvId} onValueChange={setSelectedCvId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a CV" />
                    </SelectTrigger>
                    <SelectContent>
                      {cvs.map((cv) => (
                        <SelectItem key={cv.id} value={cv.id}>
                          {cv.personal_details?.full_name || 'Untitled CV'} - {cv.cv_type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleScoreCV}
                  disabled={loading || !selectedCvId}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="score-cv-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scoring...</>
                  ) : (
                    <><BarChart3 className="w-4 h-4 mr-2" /> Score My CV</>
                  )}
                </Button>
              </div>
            </div>

            {cvScore && (
              <div className="space-y-4">
                {/* Overall Score */}
                <div className={`rounded-2xl p-6 text-white ${
                  cvScore.percentage >= 80 ? 'bg-gradient-to-br from-emerald-600 to-emerald-700' :
                  cvScore.percentage >= 60 ? 'bg-gradient-to-br from-amber-500 to-amber-600' :
                  'bg-gradient-to-br from-red-500 to-red-600'
                }`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-white/80 text-sm">Overall CV Score</p>
                      <p className="text-5xl font-bold">{cvScore.percentage}%</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{cvScore.rating}</p>
                      <p className="text-white/80 text-sm">{cvScore.total_score}/{cvScore.max_score} points</p>
                    </div>
                  </div>
                  <p className="text-white/90">{cvScore.rating_message}</p>
                  <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                    cvScore.ats_ready ? 'bg-white/20' : 'bg-white/10'
                  }`}>
                    {cvScore.ats_ready ? (
                      <><CheckCircle className="w-4 h-4" /> ATS Ready</>
                    ) : (
                      <><AlertCircle className="w-4 h-4" /> Needs Improvement for ATS</>
                    )}
                  </div>
                </div>

                {/* Section Scores */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                  <h3 className="font-bold text-slate-900 mb-4">Section Breakdown</h3>
                  <div className="space-y-4">
                    {Object.entries(cvScore.scores).map(([key, section]) => {
                      const percentage = Math.round((section.score / section.max) * 100);
                      return (
                        <div key={key}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-slate-700">{section.label}</span>
                            <span className="text-sm text-slate-500">{section.score}/{section.max}</span>
                          </div>
                          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                percentage >= 80 ? 'bg-emerald-500' :
                                percentage >= 60 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Suggestions */}
                {cvScore.suggestions?.length > 0 && (
                  <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
                    <h3 className="font-bold text-amber-800 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Top Improvements
                    </h3>
                    <div className="space-y-3">
                      {cvScore.suggestions.map((sug, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 text-amber-800 text-sm font-bold">
                            {i + 1}
                          </div>
                          <p className="text-amber-800 text-sm">{sug.tip}</p>
                        </div>
                      ))}
                    </div>
                    <Button
                      onClick={() => navigate(`/cv-builder/${selectedCvId}`)}
                      className="mt-4 bg-amber-600 hover:bg-amber-700"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Edit CV Now
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default CVEnhancerPage;
